import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useLocationTracking } from '../../hooks'
import type { RecoveryObject } from '../../api'
import { LocationClock, type ClockSettings, type ClockFace } from './LocationClock'
import { LocationColumn } from './LocationColumn'
import type { ChangeState } from './RecoveryObjectCard'

const CLOCK_SETTINGS_KEY = 'locationTracking.clockSettings'
const FACE_OPTIONS: { value: ClockFace; labelKey: string; fallback: string }[] = [
    { value: 'theme', labelKey: 'recoverRequests.locationTracking.faceTheme', fallback: 'Theme' },
    { value: 'refined-minimalist', labelKey: 'recoverRequests.locationTracking.faceRefinedMinimalist', fallback: 'Refined Minimalist' },
    { value: 'premium-blue', labelKey: 'recoverRequests.locationTracking.facePremiumBlue', fallback: 'Premium Blue' },
    { value: 'frosted-glass', labelKey: 'recoverRequests.locationTracking.faceFrostedGlass', fallback: 'Frosted Glass' },
    { value: 'dark-elegant', labelKey: 'recoverRequests.locationTracking.faceDarkElegant', fallback: 'Dark Elegant' },
]

const FLASH_MS = 500
const FLASH_COUNT = 3
const HIGHLIGHT_MS = 5000

const INTERVAL_OPTIONS = [
    { value: '0', ms: 0, labelKey: 'recoverRequests.locationTracking.refreshOff', fallback: 'Off' },
    { value: '5', ms: 5000, labelKey: 'recoverRequests.locationTracking.refresh5s', fallback: '5s' },
    { value: '10', ms: 10000, labelKey: 'recoverRequests.locationTracking.refresh10s', fallback: '10s' },
    { value: '30', ms: 30000, labelKey: 'recoverRequests.locationTracking.refresh30s', fallback: '30s' },
    { value: '60', ms: 60000, labelKey: 'recoverRequests.locationTracking.refresh1m', fallback: '1m' },
]

const objKey = (o: { locationId: number; id: string }) => `${o.locationId}:${o.id}`

export function LocationTrackingTab({ setId }: { setId: number }) {
    const { t } = useTranslation()
    const [intervalValue, setIntervalValue] = useState('0')
    const refreshMs = INTERVAL_OPTIONS.find((o) => o.value === intervalValue)?.ms ?? 0

    // Clock display settings (persisted across sessions).
    const [clock, setClock] = useState<ClockSettings>(() => {
        try {
            const saved = localStorage.getItem(CLOCK_SETTINGS_KEY)
            if (saved) {
                const merged = { face: 'theme', showSeconds: true, showAnalog: true, ...JSON.parse(saved) } as ClockSettings
                // Drop removed/unknown faces (e.g. legacy 'vault-sentinel') back to the drawn theme face.
                if (!FACE_OPTIONS.some((o) => o.value === merged.face)) merged.face = 'theme'
                return merged
            }
        } catch { /* ignore */ }
        return { face: 'theme', showSeconds: true, showAnalog: true }
    })
    useEffect(() => {
        try { localStorage.setItem(CLOCK_SETTINGS_KEY, JSON.stringify(clock)) } catch { /* ignore */ }
    }, [clock])

    const { data, isLoading, isError, refetch, isFetching } = useLocationTracking(setId, { refreshMs })

    // ── Change detection + flash orchestration ─────────────────────────────
    const [changed, setChanged] = useState<Map<string, ChangeState>>(new Map())
    const prevRef = useRef<RecoveryObject[] | null>(null)
    const timers = useRef<number[]>([])
    useEffect(() => () => { timers.current.forEach((id) => clearTimeout(id)) }, [])

    useEffect(() => {
        if (!data) return
        const objects = data.objects || []
        const prev = prevRef.current
        prevRef.current = objects
        if (prev === null) return // never flash on first load

        const prevByKey = new Map(prev.map((o) => [objKey(o), o]))
        const changedKeys: string[] = []
        for (const o of objects) {
            const old = prevByKey.get(objKey(o))
            if (!old || old.engineer !== o.engineer || old.status !== o.status || old.tasksCompleted !== o.tasksCompleted) {
                changedKeys.push(objKey(o))
            }
        }
        if (changedKeys.length === 0) return

        setChanged((m) => {
            const n = new Map(m)
            changedKeys.forEach((k) => n.set(k, 'flash'))
            return n
        })
        const tHi = window.setTimeout(() => {
            setChanged((m) => {
                const n = new Map(m)
                changedKeys.forEach((k) => n.set(k, 'highlight'))
                return n
            })
            const tClear = window.setTimeout(() => {
                setChanged((m) => {
                    const n = new Map(m)
                    changedKeys.forEach((k) => n.delete(k))
                    return n
                })
            }, HIGHLIGHT_MS)
            timers.current.push(tClear)
        }, FLASH_MS * FLASH_COUNT)
        timers.current.push(tHi)
    }, [data])

    // ── Group objects under their location ─────────────────────────────────
    const locations = data?.locations ?? []
    const objectsByLocation = useMemo(() => {
        const map = new Map<number, RecoveryObject[]>()
        for (const o of data?.objects ?? []) {
            const arr = map.get(o.locationId) || []
            arr.push(o)
            map.set(o.locationId, arr)
        }
        return map
    }, [data])

    const changeFor = (o: RecoveryObject): ChangeState => changed.get(objKey(o)) ?? null

    return (
        <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center justify-between gap-2">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        {t('recoverRequests.locationTracking.title', 'Location Tracking')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {t('recoverRequests.locationTracking.description', 'Local time and live engineer/runbook progress per recovery location.')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={intervalValue} onValueChange={setIntervalValue}>
                        <SelectTrigger className="w-28">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {INTERVAL_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {t(o.labelKey, o.fallback)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                        <span className="ml-1.5">{t('common.refresh', 'Refresh')}</span>
                    </Button>
                </div>
            </div>

            {/* Clock display options */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-muted/20 px-3 py-2">
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">{t('recoverRequests.locationTracking.clockFace', 'Clock face')}</Label>
                    <Select value={clock.face} onValueChange={(v) => setClock((c) => ({ ...c, face: v as ClockFace }))}>
                        <SelectTrigger className="h-8 w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FACE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {t(o.labelKey, o.fallback)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Switch id="lt-analog" checked={clock.showAnalog} onCheckedChange={(v) => setClock((c) => ({ ...c, showAnalog: v }))} />
                    <Label htmlFor="lt-analog" className="text-xs text-muted-foreground cursor-pointer">
                        {t('recoverRequests.locationTracking.analogClock', 'Analog clock')}
                    </Label>
                </div>
                <div className="flex items-center gap-2">
                    <Switch id="lt-seconds" checked={clock.showSeconds} onCheckedChange={(v) => setClock((c) => ({ ...c, showSeconds: v }))} />
                    <Label htmlFor="lt-seconds" className="text-xs text-muted-foreground cursor-pointer">
                        {t('recoverRequests.locationTracking.showSeconds', 'Show seconds')}
                    </Label>
                </div>
            </div>

            {isLoading ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {t('common.loading', 'Loading…')}
                </div>
            ) : isError ? (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-8 text-center text-sm text-destructive">
                    {t('recoverRequests.locationTracking.error', 'Could not load location tracking data.')}
                </div>
            ) : locations.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.locationTracking.empty', 'No recovery locations for this set.')}
                </div>
            ) : (
                <>
                    {/* Sticky clocks row */}
                    <div className="sticky top-0 z-10 -mx-1 rounded-lg border bg-background/95 px-1 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                        <div className="flex gap-4 overflow-x-auto pb-1">
                            {locations.map((loc) => (
                                <LocationClock key={loc.id} location={loc} settings={clock} />
                            ))}
                        </div>
                    </div>

                    {/* Engineer-update columns */}
                    <div className="flex gap-4 overflow-x-auto pb-2">
                        {locations.map((loc) => (
                            <LocationColumn
                                key={loc.id}
                                location={loc}
                                objects={objectsByLocation.get(loc.id) ?? []}
                                changeFor={changeFor}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
